import { $createParagraphNode, $createTextNode, type LexicalNode } from 'lexical';
import { $dfs } from '@lexical/utils';
import { LinkNode, $isLinkNode } from '@lexical/link';
import { CodeNode, $isCodeHighlightNode } from '@lexical/code';
import { TableNode, $isTableRowNode, $isTableCellNode } from '@lexical/table';

/** Moves every child of `node` to sit before it, then removes the now-empty
 *  node — the text survives, the wrapper does not. */
function $unwrapNode(node: LinkNode | TableNode | CodeNode): void {
  for (const child of node.getChildren()) {
    node.insertBefore(child);
  }
  node.remove();
}

/** The editor offers no link tool, so any link — pasted, dragged in, or in a
 *  restored draft — is dropped to plain text, keeping only its label. Runs as a
 *  node transform so every path that could introduce a link is covered. */
export function $stripLink(link: LinkNode): void {
  $unwrapNode(link);
}

/** Explicit whole-tree sweep for the read-only ScriptDocRenderer, which
 *  parses and displays a doc but never mounts DocHygienePlugin (it has no
 *  editing surface to protect). Run once right after `setEditorState`
 *  instead of registering a node transform there: a link/autolink reaching
 *  storage through any path other than this editor (a migration bug, a
 *  direct write, a future import) must never render live on this surface,
 *  which has no auth in front of it. */
export function $stripAllLinks(root: LexicalNode): void {
  for (const { node } of $dfs(root)) {
    if ($isLinkNode(node)) {
      $stripLink(node);
    }
  }
}

/** Collects the block children of every cell, in reading order. */
function $tableCellBlocks(table: TableNode): LexicalNode[] {
  const blocks: LexicalNode[] = [];
  for (const row of table.getChildren()) {
    if (!$isTableRowNode(row)) continue;
    for (const cell of row.getChildren()) {
      if (!$isTableCellNode(cell)) continue;
      blocks.push(...cell.getChildren());
    }
  }
  return blocks;
}

/** The toolbar offers no table tools, so a pasted table would be uneditable.
 *  Flatten it: each cell's blocks become top-level blocks where the table was. */
export function $flattenTable(table: TableNode): void {
  for (const block of $tableCellBlocks(table)) {
    table.insertBefore(block);
  }
  table.remove();
}

/** Same reasoning as tables: a pasted code block becomes an ordinary
 *  paragraph, keeping its text and line breaks but dropping highlight tokens. */
export function $codeBlockToParagraph(code: CodeNode): void {
  const paragraph = $createParagraphNode();
  for (const child of code.getChildren()) {
    const plain = $isCodeHighlightNode(child) ? $createTextNode(child.getTextContent()) : child;
    paragraph.append(plain);
  }
  code.replace(paragraph);
}
